/**
 * Vorlesen über die Web-Speech-API. Abkürzungen werden vorher ausgeschrieben
 * (siehe speechText in @cookbook/domain), die Stimme ist deutsch, sofern das
 * Gerät eine hat.
 */
import { speechText } from '@cookbook/domain';
import { useCallback, useEffect, useState } from 'react';

/** Zustand und Steuerung der Sprachausgabe. */
export interface Speech {
  readonly supported: boolean;
  readonly speaking: boolean;
  speak: (text: string) => void;
  stop: () => void;
}

/** Bevorzugte deutsche Stimme des Geräts. */
function germanVoice(): SpeechSynthesisVoice | undefined {
  const voices = window.speechSynthesis.getVoices();
  return (
    voices.find((voice) => voice.lang === 'de-DE') ?? voices.find((voice) => voice.lang.startsWith('de'))
  );
}

/** Sprachausgabe für eine Seite; endet beim Verlassen der Seite. */
export function useSpeech(): Speech {
  const supported = typeof window !== 'undefined' && 'speechSynthesis' in window;
  const [speaking, setSpeaking] = useState(false);

  const stop = useCallback(() => {
    if (supported) {
      window.speechSynthesis.cancel();
    }
    setSpeaking(false);
  }, [supported]);

  const speak = useCallback(
    (text: string) => {
      if (!supported || text.trim() === '') {
        return;
      }
      window.speechSynthesis.cancel();
      const utterance = new SpeechSynthesisUtterance(speechText(text));
      utterance.lang = 'de-DE';
      const voice = germanVoice();
      if (voice !== undefined) {
        utterance.voice = voice;
      }
      utterance.rate = 0.95;
      utterance.onend = () => setSpeaking(false);
      utterance.onerror = () => setSpeaking(false);
      setSpeaking(true);
      window.speechSynthesis.speak(utterance);
    },
    [supported],
  );

  useEffect(() => () => stop(), [stop]);

  return { supported, speaking, speak, stop };
}
